import React, { useEffect, useState } from 'react';
import { FaUser } from 'react-icons/fa6';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import validator from 'email-validator';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useSelector } from 'react-redux';
import countryList from 'react-select-country-list';
import { CommunityInvite, CustomFieldType } from '../../../models/inviteService.model';
import { LocalContactInfo } from '../../../models/localContactService.model';
import {
  createEmptyField,
  createEmptyLocalContact,
} from '../../../utils/formatUtils';
import localContactService from '../../../services/community/localContactService';
import ModalLayout from '../layouts/ModalLayout';
import NameInput from '../forms/profile/NameInput';
import { sortProfileFields } from '../../../utils/sortUtils';
import ProfileFieldForm from '../forms/ProfileFieldForm';
import CustomFieldsForm from '../forms/CustomFieldsForm';
import CommunicationNameInput from '../forms/profile/CommunicationNameInput';
import { isEmptyField, isValidLocalContact } from '../../../utils/validationUtils';
import CancelSaveButton from '../misc/CancelSaveButton';
import ImageInput from '../forms/ImageInput';
import IconBadge from '../icons/IconBadge';
import { ProfileDataType, ProfileField } from '../../../types/Profile';
import { Connection } from '../../../redux/slices/connectionsSlice';
import { RootState } from '../../../redux/reducers';
import WarningModal from './WarningModal';

interface LocalContactModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  invite: CommunityInvite;
  connection?: Connection;
}

export default function LocalContactModal({ open, setOpen, invite, connection }: LocalContactModalProps) {
  const { t } = useTranslation();

  const connections = useSelector((state: RootState) => state.connections);

  const [localContact, setLocalContact] = useState<LocalContactInfo>(createEmptyLocalContact());
  const [image, setImage] = React.useState<string>();
  const [imageAction, setImageAction] = React.useState<'UPLOAD' | 'DELETE'>();
  const [validate, setValidate] = useState(false);
  const [sentInvite, setSentInvite] = useState(false);
  const [emailConflictError, setEmailConflictError] = useState<string | undefined>(undefined);
  const [duplicateNameWarningOpen, setDuplicateNameWarningOpen] = useState(false);

  // linked (child) external members keep their name on the root external member in the main
  // dashboard, so the name cannot be edited from here
  const isLinkedMember = connection?.parentExternalMemberId != null;

  useEffect(() => {
    const fetchConnection = async () => {
      if (!open) return;

      setEmailConflictError(undefined);
      setDuplicateNameWarningOpen(false);

      if (connection) {
        const allFields = [...invite.mandatoryFields, ...invite.optionalFields];

        if (connection.picture) setImage(connection.picture);
        else setImage(undefined);

        const populatedFields = allFields.map((fieldType) => {
          const emptyField = createEmptyField(fieldType);
          
          if (emptyField.dataType === ProfileDataType.EMAIL && connection.fields.EMAIL) {
            return { ...emptyField, email: connection.fields.EMAIL };
          }

          if (emptyField.dataType === ProfileDataType.BUSINESSNAME && connection.fields.BUSINESSNAME) {
            return { ...emptyField, description: connection.fields.BUSINESSNAME };
          }

          if (emptyField.dataType === ProfileDataType.BIRTHDATE && connection.fields.BIRTHDATE) {
            const birthDateParts = connection.fields.BIRTHDATE.split('-');
            let convertedBirthDate = connection.fields.BIRTHDATE;
            if (birthDateParts.length === 3 && birthDateParts[0].length === 4) {
              convertedBirthDate = `${birthDateParts[2]}-${birthDateParts[1]}-${birthDateParts[0]}`;
            }
            return { ...emptyField, birthDate: convertedBirthDate };
          }

          if (emptyField.dataType === ProfileDataType.ADDRESS && connection.fields.ADDRESS) {
            try {
              const addressData = JSON.parse(connection.fields.ADDRESS);
              const countryCode = addressData.countryCode || 'NL';
              const countryLabel = countryList().getLabel(countryCode) || addressData.country || 'Netherlands';
              return { 
                ...emptyField, 
                address: {
                  ...addressData,
                  countryCode,
                  country: countryLabel,
                }
              };
            } catch {
              const lines = connection.fields.ADDRESS.split('\n');
              if (lines.length >= 2) {
                const [streetLine, cityLine, countryLine] = lines;
                const streetMatch = streetLine.match(/^(.+?)\s+(\d+)(.*)$/);
                const cityMatch = cityLine?.match(/^(\S+)\s+(.+)$/);
                const countryCode = 'NL'; // Default to Netherlands
                const countryLabel = countryLine || countryList().getLabel(countryCode) || 'Netherlands';
                
                return {
                  ...emptyField,
                  address: {
                    street: streetMatch?.[1] || '',
                    houseNumber: streetMatch?.[2] || '',
                    houseNumberAddition: streetMatch?.[3]?.trim() || '',
                    postCode: cityMatch?.[1] || '',
                    city: cityMatch?.[2] || '',
                    country: countryLabel,
                    countryCode,
                  },
                };
              }
              return emptyField;
            }
          }

          if (emptyField.dataType === ProfileDataType.PHONENUMBER && connection.fields.PHONENUMBER) {
            try {
              const phoneData = JSON.parse(connection.fields.PHONENUMBER);
              return { ...emptyField, phoneNumber: phoneData };
            } catch {
              const parts = connection.fields.PHONENUMBER.trim().split(' ');
              return {
                ...emptyField,
                phoneNumber: {
                  prefix: parts[0] || '',
                  suffix: parts.slice(1).join(' ') || '',
                  countryCode: '',
                },
              };
            }
          }
          
          return emptyField;
        });

        setLocalContact({
          firstName: connection.fields.FIRST_NAME || '',
          lastName: connection.fields.LAST_NAME || '',
          note: connection.note || '',
          receivedData: populatedFields,
          customFieldResponses: {},
        });
      } else {
        setImage(undefined);
        setValidate(false);
        setImageAction('UPLOAD');
        setLocalContact({
          ...createEmptyLocalContact(),
          receivedData: [
            ...(invite.mandatoryFields || []),
            ...(invite.optionalFields || []),
          ].map(createEmptyField),
        });
      }
    };

    fetchConnection();
  }, [connection, open]);

  const findEmailConflict = (email: string | undefined): Connection | undefined => {
    const normalizedEmail = email?.trim().toLowerCase();
    if (!normalizedEmail) return undefined;

    return connections.find(
      (c) =>
        c.dashboardMemberId !== connection?.dashboardMemberId &&
        c.fields.EMAIL?.trim().toLowerCase() === normalizedEmail,
    );
  };

  const findNameConflict = (firstName: string, lastName: string): Connection | undefined => {
    const normalizedName = `${firstName} ${lastName}`.trim().toLowerCase();
    if (!normalizedName) return undefined;

    return connections.find(
      (c) =>
        c.dashboardMemberId !== connection?.dashboardMemberId &&
        `${c.fields.FIRST_NAME} ${c.fields.LAST_NAME}`.trim().toLowerCase() === normalizedName,
    );
  };

  const performSave = async (): Promise<void> => {
    const receivedDataForSave = localContact.receivedData.map((field) => {
      if (field.dataType === ProfileDataType.BIRTHDATE && field.birthDate) {
        const [day, month, year] = field.birthDate.split('-');
        return { ...field, birthDate: `${year}-${month}-${day}` };
      }
      return field;
    });

    try {
      let contactExternalMemberId = connection?.externalMemberId;
      if (connection) {
        await localContactService.updateLocalContact(
          connection.externalMemberId!,
          localContact.firstName,
          localContact.lastName,
          localContact.note,
          receivedDataForSave,
          localContact.customFieldResponses,
        );
      } else {
        const createdContact = await localContactService
          .createLocalContacts([
            {
              ...localContact,
              receivedData: receivedDataForSave.filter((f) => !isEmptyField(f)),
            },
          ])
          .then((res) => res?.[0]);
        contactExternalMemberId = createdContact.externalMemberId;
      }

      if (sentInvite && contactExternalMemberId){
        localContactService.inviteContacts([contactExternalMemberId]).then(() => toast.success(t('toast.success.inviteSent_one')));
      }

      if (imageAction === 'UPLOAD' && image)
        await localContactService.uploadProfilePicture(
          contactExternalMemberId!,
          await fetch(image!).then((res) => res.blob() as Promise<File>),
        );
      else if (imageAction === 'DELETE' && contactExternalMemberId)
        await localContactService.deleteProfilePicture(contactExternalMemberId);

      setOpen(false);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 400) {
        const backendMessage: string | undefined =
          error.response?.data?.message || error.response?.data?.error;
        if (backendMessage?.toLowerCase().includes('already exists in this dashboard')) {
          setEmailConflictError(t('component.modal.localContact.emailAlreadyExists'));
          return;
        }
      }
      toast.error(t('toast.error.unexpected'));
    }
  };

  const handleSave = async () => {
    setValidate(true);
    setEmailConflictError(undefined);
    if (!isValidLocalContact(localContact, invite)) return;

    const emailField = localContact.receivedData.find((f) => f.dataType === ProfileDataType.EMAIL);
    const emailConflict = findEmailConflict(emailField?.email);
    if (emailConflict) {
      setEmailConflictError(t('component.modal.localContact.emailAlreadyExists'));
      return;
    }

    const nameConflict = findNameConflict(localContact.firstName, localContact.lastName);
    if (nameConflict) {
      setDuplicateNameWarningOpen(true);
      return;
    }

    await performSave();
  };

  const communicationName = invite.customFields?.find(
    (f) => f.type === CustomFieldType.COMMUNICATION_NAME,
  );

  const handleFieldChangeByType = (newField: ProfileField) => {
    const receivedData =
      newField.dataType === ProfileDataType.SOCIAL
        ? localContact.receivedData.map((oldField) =>
            oldField.social?.socialType === newField.social?.socialType ? newField : oldField,
          )
        : localContact.receivedData.map((oldField) =>
            oldField.dataType === newField.dataType ? newField : oldField,
          );
    setLocalContact({ ...localContact, receivedData });

    if (newField.dataType === ProfileDataType.EMAIL && emailConflictError) {
      setEmailConflictError(undefined);
    }
  };

  return (
    <ModalLayout
      open={open}
      setOpen={setOpen}
      closeButton
      className="z-50 mx-6 my-8 w-full rounded-[12px] bg-secondary-50 p-8 sm:max-w-[700px]">
      <div className="flex border-b border-secondary-200">
        <IconBadge icon={FaUser} />

        <div className="mb-4 ml-4 flex w-full flex-col">
          <h2 className="text-2xl">
            {connection
              ? t('component.modal.localContact.edit.title')
              : t('component.modal.localContact.create.title')}
          </h2>
          <p>
            {connection
              ? t('component.modal.localContact.edit.subtitle')
              : t('component.modal.localContact.create.subtitle')}
          </p>
        </div>
      </div>
      <div className="mt-4 flex w-full flex-col gap-4">
        <ImageInput
          className="h-32 w-32"
          iconClassName="w-14 h-14"
          image={image}
          setImage={setImage}
          setImageAction={setImageAction}
        />

        <NameInput
          alias={`${localContact.firstName || ''}+${localContact.lastName || ''}`}
          validate={validate}
          disabled={isLinkedMember}
          setAlias={(s) => {
            const [firstName, lastName] = s.split('+');
            setLocalContact({ ...localContact, firstName, lastName });
          }}
        />
        {isLinkedMember && (
          <p className="text-sm text-gray-500">
            {t('component.modal.localContact.linkedNameLocked')}
          </p>
        )}

        {communicationName && (
          <CommunicationNameInput
            name={localContact.customFieldResponses[communicationName?.customFieldId!] || ''}
            setName={(newField) =>
              setLocalContact({
                ...localContact,
                customFieldResponses: {
                  ...localContact.customFieldResponses,
                  [communicationName?.customFieldId!]: newField,
                },
              })
            }
          />
        )}
        {sortProfileFields(localContact.receivedData).map((f, index) => {
          const isEmail =
            f.dataType === ProfileDataType.EMAIL && f.email && validator.validate(f.email);

          return (
            <React.Fragment key={index}>
              <ProfileFieldForm
                field={f}
                canBeEmpty
                validate={validate}
                setField={handleFieldChangeByType}
              />
              {isEmail && (
                <div className="flex items-center gap-2 pl-1 mt-1">
                  <input
                    type="checkbox"
                    id="useMainEmail"
                    checked={sentInvite}
                    onChange={(e) => setSentInvite(e.target.checked)}
                    className="accent-primary-500"
                    value={connection?.fields.EMAIL}
                  />
                  <label htmlFor="useMainEmail" className="text-sm">
                    {t('component.modal.localContact.sendInvite')}
                  </label>
                </div>
              )}
            </React.Fragment>
          );
        })}

        <CustomFieldsForm
          fields={invite.customFields || []}
          response={localContact.customFieldResponses}
          setResponse={(newResponse) =>
            setLocalContact({ ...localContact, customFieldResponses: newResponse })
          }
        />

        {/* Description */}
        <div className="flex flex-col gap-2">
          <p className="mb-2 text-lg font-medium">{t('component.modal.localContact.noteTitle')}</p>
          <textarea
            className="mb-2 h-32 w-full resize-none rounded-lg placeholder-secondary-200"
            placeholder="Schrijf hier je opmerkingen..."
            onChange={(e) => setLocalContact({ ...localContact, note: e.target.value })}
            value={localContact.note}
          />
        </div>

        {/* Triggers rerender of table when assigning so can only be added once we deal with rerender issue */}
        {/* {connection && <TagDisplay connection={connection} withHover={!isMobile} />} */}
      </div>
      <div className="flex w-full flex-1 flex-col items-end gap-2">
        <p
          className={classNames('w-fit justify-end text-sm text-danger', {
            invisible: !validate || isValidLocalContact(localContact, invite),
          })}>
          {t('component.modal.localContact.invalid')}
        </p>
        {emailConflictError && (
          <p className="w-fit justify-end text-sm text-danger">{emailConflictError}</p>
        )}

        <CancelSaveButton onCancel={() => setOpen(false)} onSave={handleSave} />
      </div>
      <WarningModal
        open={duplicateNameWarningOpen}
        setOpen={setDuplicateNameWarningOpen}
        title={t('component.modal.localContact.duplicateNameWarning.title')}
        description={t('component.modal.localContact.duplicateNameWarning.description', {
          name: `${localContact.firstName} ${localContact.lastName}`.trim(),
        })}
        button={t('component.modal.localContact.duplicateNameWarning.button')}
        cancel={t('general.cancel')}
        onContinue={() => performSave()}
      />
    </ModalLayout>
  );
}
